import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query'
import { meQueryKey } from '../auth/session'
import { BffError, isUnauthenticated } from '../bff/errors'

/**
 * Defaults tuned for an API that will answer in 1-3 seconds once it is
 * BigQuery-backed rather than an in-memory mock.
 *
 *  - `staleTime` of 30s: navigating away and back must not re-run a
 *    multi-second query the user just paid for.
 *  - Retries are deliberately narrow. Retrying a 4xx is pointless (it will
 *    fail identically) and retrying a 401 races the session ending;
 *    retrying an expensive 5xx three times turns one slow query into four.
 *  - `refetchOnWindowFocus` off: on a reconciliation screen the user tabs to
 *    a spreadsheet and back constantly, and silently re-running the query
 *    each time both costs real money against BigQuery's per-query billing
 *    and makes the table appear to flicker for no reason the user caused.
 */
export function createQueryClient(): QueryClient {
  /*
   * A 401 from anywhere ends the session in the SPA (MPAY-32193).
   *
   * The BFF's idle timeout (SESSION_IDLE_SECONDS, 30 minutes) and Keycloak's
   * ssoSessionIdleTimeout both expire on their own, and the BFF then answers
   * 401 correctly. Nothing here reacted to that, so the shell stayed on screen
   * and the user went on clicking around an application that could no longer
   * answer them — which reads as the portal being broken rather than as their
   * session having ended.
   *
   * Invalidating `me` is the whole fix: SessionProvider re-asks /bff/me,
   * reports 'anonymous', and RequireSession renders the landing screen IN
   * PLACE — so the URL the user was on survives, and signing in returns them
   * to it (see Landing's returnTo).
   *
   * It cannot loop. /bff/me answers 200 for an anonymous caller and never
   * 401s, so the refetch this triggers comes back `authenticated: false`
   * rather than another error. That property is load-bearing; a /bff/me that
   * started answering 401 would turn this into a spin.
   *
   * `client` is assigned before any query can settle — the first fetch cannot
   * start until React has rendered a provider holding the returned client —
   * so the definite-assignment assertion is a fact about ordering, not a
   * guess.
   */
  let client!: QueryClient
  const endSessionOn401 = (error: unknown) => {
    if (!isUnauthenticated(error)) return
    void client.invalidateQueries({ queryKey: meQueryKey })
  }

  client = new QueryClient({
    queryCache: new QueryCache({ onError: endSessionOn401 }),
    mutationCache: new MutationCache({ onError: endSessionOn401 }),
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: false,
        retry: (failureCount, error) => {
          if (isUnauthenticated(error)) return false
          if (error instanceof BffError && error.status < 500) return false
          return failureCount < 1
        },
      },
      mutations: { retry: false },
    },
  })
  return client
}
